import React, { useState, useRef, useEffect } from "react";
import { SketchPicker } from "react-color";
import { COLORS } from "./constants";

export default function SketchColorPicker({
  color = "#ffffff",
  onChange,
  size = 20,
  title = "Custom Color Picker"
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const [alignRight, setAlignRight] = useState(false);
  const [tempColor, setTempColor] = useState(color);
  const [initialColor, setInitialColor] = useState(color);
  const btnRef = useRef(null);

  useEffect(() => {
    setTempColor(color);
  }, [color]);

  const toggleOpen = (e) => {
    e.stopPropagation();
    if (!isOpen && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const pickerH = 270;
      const pickerW = 230;

      // Check vertical space (open upward if near bottom of screen)
      const spaceBelow = window.innerHeight - rect.bottom;
      setOpenUpward(spaceBelow < pickerH && rect.top > 180);

      // Check horizontal space (align right if near right edge)
      const spaceRight = window.innerWidth - rect.left;
      setAlignRight(spaceRight < pickerW);

      setTempColor(color);
      setInitialColor(color);
    }
    setIsOpen(prev => !prev);
  };

  const handleApply = (e) => {
    e.stopPropagation();
    if (onChange) onChange(tempColor);
    setIsOpen(false);
  };

  const handleCancel = (e) => {
    e.stopPropagation();
    if (onChange) onChange(initialColor);
    setIsOpen(false);
  };

  const isCustom = color && !COLORS.includes(color);

  return (
    <div style={{ position: "relative", display: "inline-flex", verticalAlign: "middle" }}>
      <button
        ref={btnRef}
        type="button"
        title={title}
        onClick={toggleOpen}
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          background: isCustom
            ? color
            : "conic-gradient(from 0deg, red, yellow, lime, aqua, blue, magenta, red)",
          border: isCustom
            ? "2.5px solid #007AFF"
            : isOpen
            ? "2px solid #007AFF"
            : "1.5px solid rgba(255,255,255,0.7)",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,
          outline: "none",
          boxShadow: isCustom
            ? `0 0 10px ${color}88`
            : isOpen
            ? "0 0 10px rgba(0,122,255,0.6)"
            : "0 1px 3px rgba(0,0,0,0.3)",
          transform: isOpen || isCustom ? "scale(1.15)" : "scale(1)",
          transition: "transform 0.15s ease, box-shadow 0.15s ease",
          boxSizing: "border-box",
        }}
      >
        <span
          style={{
            fontSize: Math.max(10, Math.round(size * 0.55)),
            fontWeight: "bold",
            color: "#fff",
            textShadow: "0 1px 2px rgba(0,0,0,0.9)",
            pointerEvents: "none",
            lineHeight: 1,
          }}
        >
          +
        </span>
      </button>

      {isOpen && (
        <>
          {/* Backdrop to close when clicking outside */}
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 99998,
              background: "transparent",
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleApply(e);
            }}
          />

          {/* SketchPicker popover positioned right next to button */}
          <div
            className="fd-sketch-picker-popover"
            style={{
              position: "absolute",
              color: "#222222",
              ...(openUpward ? { bottom: "calc(100% + 8px)" } : { top: "calc(100% + 8px)" }),
              ...(alignRight ? { right: 0 } : { left: 0 }),
              zIndex: 99999,
              background: "#fff",
              borderRadius: 12,
              padding: "10px",
              boxShadow: "0 20px 48px rgba(0,0,0,0.45), 0 4px 14px rgba(0,0,0,0.2)",
              border: "1px solid rgba(0,0,0,0.15)",
              animation: "fd-popIn 0.15s ease-out",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <style>{`
              .fd-sketch-picker-popover input {
                color: #111111 !important;
                background-color: #ffffff !important;
                box-shadow: inset 0 0 0 1px #cccccc !important;
                border-radius: 4px !important;
              }
              .fd-sketch-picker-popover label {
                color: #555555 !important;
                font-weight: 600 !important;
              }
            `}</style>
            {/* Header with Color Swatch & Hex */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 8,
                paddingBottom: 6,
                borderBottom: "1px solid #eee",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    background: tempColor,
                    border: "2px solid #fff",
                    boxShadow: "0 0 0 1.5px #007AFF, 0 2px 4px rgba(0,0,0,0.2)",
                  }}
                />
                <span style={{ fontSize: 13, fontWeight: 700, color: "#111" }}>Selected Color</span>
              </div>
              <span
                style={{
                  fontSize: 12,
                  fontFamily: "monospace",
                  color: "#111",
                  fontWeight: 700,
                  background: "#f0f2f5",
                  border: "1px solid #e1e4e8",
                  padding: "2px 8px",
                  borderRadius: 5,
                }}
              >
                {tempColor}
              </span>
            </div>

            {/* SketchPicker (Clean Hue bar, no confusing side box) */}
            <SketchPicker
              color={tempColor}
              onChange={(c) => {
                const val = c.hex ? c.hex.toUpperCase() : "#FFFFFF";
                setTempColor(val);
                // Live preview while dragging
                if (onChange) onChange(val);
              }}
              disableAlpha={true}
              styles={{
                default: {
                  picker: {
                    boxShadow: "none",
                    padding: "0",
                    background: "transparent",
                    width: "220px",
                  },
                  color: {
                    display: "none", // Removes the confusing little box right next to the hue bar
                  },
                  hue: {
                    height: "12px",
                    borderRadius: "6px",
                  },
                  Hue: {
                    radius: "6px",
                  },
                  sliders: {
                    padding: "6px 0 2px 0",
                  },
                },
              }}
            />

            {/* Action Buttons: Cancel and OK */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                marginTop: 10,
                paddingTop: 8,
                borderTop: "1px solid #eee",
              }}
            >
              <button
                type="button"
                onClick={handleCancel}
                style={{
                  padding: "6px 14px",
                  fontSize: 12,
                  borderRadius: 6,
                  border: "1px solid #d0d0d0",
                  background: "#fff",
                  color: "#555",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApply}
                style={{
                  padding: "6px 18px",
                  fontSize: 12,
                  borderRadius: 6,
                  border: "none",
                  background: "#007AFF",
                  color: "#fff",
                  fontWeight: 700,
                  cursor: "pointer",
                  boxShadow: "0 2px 6px rgba(0,122,255,0.4)",
                }}
              >
                OK
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
